import PropTypes from 'prop-types';
import React, { memo, useCallback } from 'react';
import { Map } from 'immutable';
import classNames from 'classnames';

import DropDown, { DropDownElement } from '../../helpers/DropDown';
import isFunction from '../../../utils/isFunction';
import SelectRange from '../../helpers/SelectRange';
import { BrowseAutoComplete } from '../BrowseAutoComplete';
import { BrowseAddTagButton } from '../BrowseAddTagButton';
import { BrowseCheckbox } from '../BrowseCheckbox';
import { BrowseControlContent } from '../BrowseControlContent';
import { BuyerFilterTag } from '../BuyerFilterTag';

import style from './style.module.css';

const BrowseBuyerFilter = props => {
  const {
    filter,
    info,
    rangeFilterData,
    onChange,
    onGetApprovalSuggestion,
    onGetBuyerSuggestion,
    onGetNextApprovalSuggestion,
    onGetNextBuyerSuggestion,
    onGetNextProjectSuggestion,
    onGetPrioritySuggestion,
    onGetProjectSuggestion,
    onSubFilterChange,
  } = props;

  const handleApprovedChange = useCallback(
    event => {
      const { value } = event.target;

      onChange({
        filterName: filter.get('name'),
        filterData: {
          approved: {
            name: value,
            value,
          },
        },
      });
    },
    [filter, onChange],
  );

  const handleActiveChange = useCallback(
    event => {
      const { checked } = event.target;

      onChange({
        filterName: filter.get('name'),
        filterData: {
          active: checked,
        },
      });
    },
    [filter, onChange],
  );

  const handleAddTag = useCallback(() => {
    const buyerSelected = info.get('buyer').get('selected');

    if (buyerSelected) {
      const buyerSelectedText = buyerSelected.get(filter.getIn(['buyer', 'renderField']), '');
      let projectSelectedText = '(any project)';
      let approvalSelectedText = '(any approval list)';
      let prioritySelectedText = '(any priority)';
      let approvedSelectedText = '(any approval state)';
      let activeSelectedText = 'active or inactive';

      const selectedBuyer = {
        buyer_id: buyerSelected.get(filter.getIn(['buyer', 'valueField'])),
      };

      const minCurrentStatus = info.getIn(['currentStatus', 'text', 'min']);
      const maxCurrentStatus = info.getIn(['currentStatus', 'text', 'max']);
      const minHighValue = info.getIn(['highStatus', 'text', 'min']);
      const maxHighValue = info.getIn(['highStatus', 'text', 'max']);

      selectedBuyer.current_status_range = {
        min: minCurrentStatus,
        max: maxCurrentStatus,
      };

      selectedBuyer.high_status_range = {
        min: minHighValue,
        max: maxHighValue,
      };

      const currentStatusText = `Curr: ${minCurrentStatus}-${maxCurrentStatus}`;
      const highStatusText = `High: ${minHighValue}-${maxHighValue}`;

      const projectSelected = info.get('project').get('selected');

      if (projectSelected) {
        const render = filter.getIn(['project', 'renderField']);

        selectedBuyer.project_id = projectSelected.get(filter.getIn(['project', 'valueField']));
        if (render && isFunction(isFunction)) {
          projectSelectedText = render(projectSelected.toJS());
        } else {
          projectSelectedText = projectSelected.get(render);
        }
      }

      const approvalSelected = info.get('approval').get('selected');

      if (approvalSelected) {
        selectedBuyer.approval_list_id = approvalSelected.get(filter.getIn(['approval', 'valueField']));
        approvalSelectedText = approvalSelected.get(filter.getIn(['approval', 'renderField']));
      }

      const prioritySelected = info.get('priority').get('selected');

      if (prioritySelected) {
        selectedBuyer.priority = prioritySelected.get(filter.getIn(['priority', 'valueField']));
        prioritySelectedText = prioritySelected.get(filter.getIn(['priority', 'renderField']));
      }

      switch (info.getIn(['approved', 'value'], '').toUpperCase()) {
        case 'YES':
          selectedBuyer.approved = true;
          approvedSelectedText = 'Yes';
          break;

        case 'NO':
          selectedBuyer.approved = false;
          approvedSelectedText = 'No';
          break;

        case 'NO RESPONSE':
          selectedBuyer.approved = null;
          approvedSelectedText = 'No Response';
          break;

        default:
          break;
      }

      switch (info.get('active')) {
        case null:

        // fallthrough
        case undefined:
          break;

        case true:
          selectedBuyer.active = true;
          activeSelectedText = 'active';
          break;

        case false:
          selectedBuyer.active = false;
          activeSelectedText = 'inactive';
          break;

        default:
          break;
      }

      onChange({
        filterName: filter.get('name'),
        filterData: {
          selectedList: info.get('selectedList').push(
            Map({
              tag: `${buyerSelectedText}/${currentStatusText}/${highStatusText}/${projectSelectedText}/${approvalSelectedText}/${prioritySelectedText}/${approvedSelectedText}/${activeSelectedText}`,
              exclude: false,
            }),
          ),
          selectedListParam: info.get('selectedListParam').push(selectedBuyer),
          buyer: {
            text: '',
            selected: null,
          },
          project: {
            text: '',
            selected: null,
          },
          approval: {
            text: '',
            selected: null,
          },
          priority: {
            text: '',
            selected: null,
          },
          approved: {
            name: '',
            value: '',
          },
          active: null,
        },
      });
    }
  }, [filter, info, onChange]);

  const handleRemoveTag = useCallback(
    event => {
      event.stopPropagation();

      const { name } = event.target;
      const tagIndex = parseInt(name.replace('buyerFilterTag', ''), 10);
      let selectedList = info.get('selectedList');

      selectedList = selectedList.delete(tagIndex);

      let selectedListParam = info.get('selectedListParam');

      selectedListParam = selectedListParam.delete(tagIndex);

      onChange({
        filterName: filter.get('name'),
        filterData: {
          selectedList,
          selectedListParam,
        },
      });
    },
    [filter, info, onChange],
  );

  const handleChangeStatus = useCallback(
    index => {
      const selectedList = info.get('selectedList');

      onChange({
        filterName: filter.get('name'),
        filterData: {
          selectedList: selectedList.updateIn([index, 'exclude'], exclude => !exclude),
        },
      });
    },
    [filter, info, onChange],
  );

  const handleValuesChange = useCallback(
    ({ filterName, filterData }) => {
      onSubFilterChange({
        filterName,
        filterData,
      });
    },
    [onSubFilterChange],
  );

  const approvedContent = filter.get('approved').map(approvedItem => {
    const { attributes } = approvedItem;

    return (
      <DropDownElement {...attributes} key={approvedItem.get('value')} value={approvedItem.get('value')}>
        {approvedItem.get('name')}
      </DropDownElement>
    );
  });
  const selectedList = info.get('selectedList');
  const tagListContent = selectedList
    .toJS()
    .map(({ tag, exclude }, index) => (
      <BuyerFilterTag
        key={index}
        exclude={exclude}
        index={index}
        onChangeStatus={handleChangeStatus}
        onRemoveTag={handleRemoveTag}
        value={tag}
      />
    ));

  return (
    <BrowseCheckbox {...props}>
      {rangeFilterData.length > 0 ? (
        <BrowseControlContent variant="form">
          {tagListContent}
          <div className="form-inline">
            <BrowseAutoComplete
              filter={filter.get('buyer')}
              info={info.get('buyer')}
              onChange={onSubFilterChange}
              onGetNextSuggestion={onGetNextBuyerSuggestion}
              onGetSuggestion={onGetBuyerSuggestion}
            />
            <SelectRange
              filter={filter.get('currentStatus')}
              info={info.get('currentStatus')}
              onChange={handleValuesChange}
              rangeData={rangeFilterData}
              title="Current Status"
            />
            <SelectRange
              filter={filter.get('highStatus')}
              info={info.get('highStatus')}
              onChange={handleValuesChange}
              rangeData={rangeFilterData}
              title="High Status"
            />
            <BrowseAutoComplete
              filter={filter.get('project')}
              info={info.get('project')}
              onChange={onSubFilterChange}
              onGetNextSuggestion={onGetNextProjectSuggestion}
              onGetSuggestion={onGetProjectSuggestion}
            />
            <BrowseAutoComplete
              filter={filter.get('approval')}
              info={info.get('approval')}
              onChange={onSubFilterChange}
              onGetNextSuggestion={onGetNextApprovalSuggestion}
              onGetSuggestion={onGetApprovalSuggestion}
            />
            <BrowseAutoComplete
              filter={filter.get('priority')}
              info={info.get('priority')}
              onChange={onSubFilterChange}
              onGetSuggestion={onGetPrioritySuggestion}
            />
            <div className={classNames(style.dropdownGroup, 'form-group form-group--inline')}>
              <label htmlFor="browse-approved">Approved</label>
              <DropDown
                buttonContent={info.get('approved')}
                className="btn btn-default btn-xs"
                id="approved-select"
                name="approved-select"
                onChange={handleApprovedChange}
                isValuePicker
              >
                {approvedContent}
              </DropDown>
            </div>
            <div className="checkbox-wrap">
              <input
                checked={info.get('active')}
                id="browse-buyer-active"
                onChange={handleActiveChange}
                type="checkbox"
              />
              <label htmlFor="browse-buyer-active">Active</label>
            </div>
            <BrowseAddTagButton onClick={handleAddTag} />
          </div>
        </BrowseControlContent>
      ) : (
        <p>There is no statuses, please contact the system's administrator</p>
      )}
    </BrowseCheckbox>
  );
};

BrowseBuyerFilter.propTypes = {
  filter: PropTypes.instanceOf(Map).isRequired,
  info: PropTypes.instanceOf(Map).isRequired,
  onChange: PropTypes.func.isRequired,
  onGetApprovalSuggestion: PropTypes.func.isRequired,
  onGetBuyerSuggestion: PropTypes.func.isRequired,
  onGetNextApprovalSuggestion: PropTypes.func.isRequired,
  onGetNextBuyerSuggestion: PropTypes.func.isRequired,
  onGetNextProjectSuggestion: PropTypes.func.isRequired,
  onGetPrioritySuggestion: PropTypes.func.isRequired,
  onGetProjectSuggestion: PropTypes.func.isRequired,
  onSubFilterChange: PropTypes.func.isRequired,
};

export default memo(BrowseBuyerFilter);
